import React, { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import './AuthPages.scss';

interface LoginForm {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const { login, googleLogin, isAuthenticated, isLoading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Get user type from URL (set by header toggle) or check mode parameter
  const isEmployerMode = searchParams.get('type') === 'employer' || searchParams.get('mode') === 'employer';
  const userType = isEmployerMode ? 'employer' : 'employee';
  const returnUrl = searchParams.get('return') || '/';
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [loginForm, setLoginForm] = useState<LoginForm>({
    email: '',
    password: ''
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!loginForm.email) {
      newErrors.email = 'El. paštas privalomas';
    } else if (!validateEmail(loginForm.email)) {
      newErrors.email = 'Neteisingas el. pašto formatas';
    }

    if (!loginForm.password) {
      newErrors.password = 'Slaptažodis privalomas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [loginForm]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await login(loginForm.email, loginForm.password);
      
      if (result.success) {
        const destination = returnUrl !== '/' ? returnUrl : (result.redirectPath || (isEmployerMode ? '/?mode=employer' : '/'));
        window.location.href = destination;
      } else {
        setErrors({ general: result.message });
      }
    } catch (error) {
      setErrors({ general: 'Prisijungimo klaida' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) return;
    
    setLoading(true);
    try {
      const result = await googleLogin(credentialResponse.credential, userType);
      
      if (result.success) {
        const destination = returnUrl !== '/' ? returnUrl : (result.redirectPath || (isEmployerMode ? '/?mode=employer' : '/'));
        window.location.href = destination;
      } else {
        setErrors({ general: result.message });
      }
    } catch (error) {
      setErrors({ general: 'Google prisijungimas nepavyko' });
    } finally {
      setLoading(false);
    }
  };

  const handleLoginFormChange = useCallback((field: keyof LoginForm, value: string) => {
    setLoginForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  // Redirect if already authenticated (wait for auth to initialize first)
  React.useEffect(() => {
    if (!authLoading && isAuthenticated) {
      // Redirect based on mode - employers go to employer homepage, employees to regular homepage
      const destination = isEmployerMode ? '/?mode=employer' : '/';
      window.location.href = destination;
    }
  }, [isAuthenticated, authLoading, isEmployerMode]);

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              Kraunama...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Login Card */}
        <div className="auth-card">
          <div className="auth-header">
            <h1>
              {isEmployerMode 
                ? 'Šiandien - puiki diena auginti savo komandą' 
                : 'Pasiruošę žengti kitą karjeros žingsnį?'}
            </h1>
          </div>

          {errors.general && (
            <div className="error-alert">
              {errors.general}
            </div>
          )}

          <form onSubmit={handleLogin} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">El. paštas</label>
              <input
                type="email"
                id="email"
                value={loginForm.email}
                onChange={(e) => handleLoginFormChange('email', e.target.value)}
                className={errors.email ? 'error' : ''}
                placeholder="jusu@email.com"
                autoComplete="email"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Slaptažodis</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={loginForm.password}
                  onChange={(e) => handleLoginFormChange('password', e.target.value)}
                  className={errors.password ? 'error' : ''}
                  placeholder="Jūsų slaptažodis"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-extras">
              <Link to="/forgot-password" className="forgot-link">
                Pamiršote slaptažodį?
              </Link>
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Jungiamasi...' : 'Prisijungti'}
            </button>

            <div className="divider">
              <span>arba</span>
            </div>

            <div className="google-login-wrapper">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => console.warn('Google Sign-In error')}
                text="signin_with"
                shape="rectangular"
                size="large"
              />
            </div>
          </form>

          <div className="auth-footer">
            <p>
              Neturite paskyros?{' '}
              <Link to={`/register?type=${userType}&mode=${userType}${returnUrl !== '/' ? `&return=${encodeURIComponent(returnUrl)}` : ''}`}>
                Registruokitės
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;

